import React, { useState } from 'react';
import { Box, Typography, Dialog, IconButton } from '@mui/material';
import {
  Image as ImageIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';

import { Widget, ImageWidgetConfiguration } from '../../../types/canvas.types';
import { WidgetWrapper } from './WidgetWrapper';

interface ImageWidgetProps {
  widget: Widget;
  onEdit?: (widgetId: string) => void;
  onDelete?: (widgetId: string) => void;
  onDuplicate?: (widgetId: string) => void;
}

export const ImageWidget: React.FC<ImageWidgetProps> = ({
  widget,
  onEdit,
  onDelete,
  onDuplicate,
}) => {
  const config = widget.configuration as ImageWidgetConfiguration;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  const imageUrl = config?.image_url;
  const altText = config?.alt_text || config?.title || 'Image';
  const caption = config?.caption;
  const fitMode = config?.fit_mode || 'contain';
  const lightboxEnabled = config?.lightbox_enabled !== false;

  const objectFitMap: Record<string, React.CSSProperties['objectFit']> = {
    contain: 'contain',
    cover: 'cover',
    fill: 'fill',
  };

  if (!imageUrl) {
    return (
      <WidgetWrapper
        widget={widget}
        onEdit={onEdit}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
          textAlign="center"
        >
          <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            No image configured
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Edit this widget to set an image URL
          </Typography>
        </Box>
      </WidgetWrapper>
    );
  }

  return (
    <WidgetWrapper
      widget={widget}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 150,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            cursor: lightboxEnabled ? 'pointer' : 'default',
            overflow: 'hidden',
            '&:hover .zoom-icon': lightboxEnabled ? { opacity: 1 } : {},
          }}
          onClick={() => lightboxEnabled && setLightboxOpen(true)}
        >
          {imageError ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              minHeight={150}
            >
              <ImageIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
              <Typography variant="body2" color="error">
                Failed to load image
              </Typography>
            </Box>
          ) : (
            <Box
              component="img"
              src={imageUrl}
              alt={altText}
              onError={() => setImageError(true)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: objectFitMap[fitMode] || 'contain',
                display: 'block',
              }}
            />
          )}

          {lightboxEnabled && !imageError && (
            <Box
              className="zoom-icon"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                opacity: 0,
                transition: 'opacity 0.2s',
                bgcolor: 'rgba(0,0,0,0.5)',
                borderRadius: '50%',
                p: 0.5,
              }}
            >
              <ZoomInIcon sx={{ color: 'white', fontSize: 20 }} />
            </Box>
          )}
        </Box>

        {caption && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, textAlign: 'center', px: 1 }}
          >
            {caption}
          </Typography>
        )}
      </Box>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            maxWidth: '90vw',
            maxHeight: '90vh',
          },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={imageUrl}
            alt={altText}
            sx={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
      </Dialog>
    </WidgetWrapper>
  );
};
